//
// # Keyboard Shortcut Data
//
// Single source of truth for keyboard/mouse shortcuts.
// Used by:
//   - KeyboardHelpPanel.tsx (in-app help)
//   - docs-generate.ts (generates keyboard-shortcuts.md)
//

// ContextScope inlined here to avoid circular dependency when this file is
// imported by scripts/docs-compile.ts (editorContext.ts pulls in workspace.ts
// which pulls in most of the app).
type ContextScope = "global" | "schematic" | "symbol" | "layout" | "route";

export interface ShortcutEntry {
  readonly key: string;
  readonly description: string;
}

export interface ShortcutCategory {
  readonly name: string;
  // Editing contexts this category is relevant to. The help panel shows a
  // category when any of its scopes matches the current context (or always,
  // when contextual mode is off). See editorContext.ts. Categories scoped
  // "global" are shown in every context.
  readonly scopes: ReadonlyArray<ContextScope>;
  readonly shortcuts: ReadonlyArray<ShortcutEntry>;
}

export const shortcutCategories: ReadonlyArray<ShortcutCategory> = [
  {
    name: "General",
    scopes: ["global"],
    shortcuts: [
      { key: "?", description: "Toggle this help" },
      { key: "T", description: "Toggle theme (Auto/Light/Dark)" },
      { key: "F", description: "Zoom to fit" },
      { key: "Z", description: "Zoom to box" },
      { key: "Esc", description: "Cancel / abort" },
      { key: "Ctrl/Cmd+Z", description: "Undo" },
      { key: "Ctrl/Cmd+Shift+Z", description: "Redo" },
      { key: "L", description: "Toggle layer info" },
      { key: "A", description: "Show all layers" },
      { key: "Shift+G", description: "Toggle grid" },
      { key: "Shift+O", description: "Toggle outlines" },
      { key: "M", description: "Ruler / measure" },
      { key: "Ctrl/Cmd+Shift+E", description: "Toggle parasitics" },
      { key: "Ctrl/Cmd+Shift+O", description: "Parasitics summary" },
      {
        key: "Ctrl/Cmd+Shift+L",
        description: "Create layout from schematic",
      },
    ],
  },
  {
    name: "Selection",
    scopes: ["schematic", "layout", "symbol"],
    shortcuts: [
      { key: "Click", description: "Select entity" },
      { key: "Shift+Click", description: "Add/remove from selection" },
      { key: "Drag (empty)", description: "Box-select" },
      { key: "Shift+Drag", description: "Add to selection (box)" },
      { key: "B", description: "Box-select mode" },
      { key: "G", description: "Grab / move selection" },
      { key: "Del", description: "Delete selected" },
      { key: "Ctrl/Cmd+A", description: "Select all" },
    ],
  },
  {
    name: "Schematic",
    scopes: ["schematic"],
    shortcuts: [
      { key: "I", description: "Add instance" },
      { key: "W", description: "Draw wire" },
      { key: "P", description: "Add pin" },
      { key: "E", description: "Add rectangle" },
      { key: "Shift+C", description: "Clean up wires" },
      { key: "Drag", description: "Move instance/pin with connected wires" },
      {
        key: "Ctrl/Cmd+Drag",
        description: "Move instance/pin only (leave wires)",
      },
      {
        key: "Ctrl/Cmd+C",
        description: "Copy selected instances, wires, pins",
      },
      { key: "Ctrl/Cmd+V", description: "Paste from clipboard" },
    ],
  },
  {
    name: "Symbol",
    scopes: ["symbol"],
    shortcuts: [
      { key: "P", description: "Add pin" },
      { key: "E", description: "Draw rectangle" },
      { key: "Y", description: "Draw polygon" },
      { key: "U", description: "Draw path" },
      { key: "Shift+E", description: "Draw ellipse" },
      { key: "Shift+C", description: "Draw arc" },
    ],
  },
  {
    name: "Layout",
    scopes: ["layout"],
    shortcuts: [{ key: "Shift+R", description: "Enter route mode" }],
  },
  {
    name: "Placement",
    scopes: ["schematic", "layout", "symbol"],
    shortcuts: [
      { key: "R", description: "Rotate" },
      { key: "H", description: "Flip horizontal" },
      { key: "V", description: "Flip vertical" },
      { key: "I / O / X", description: "Pin direction" },
    ],
  },
  {
    name: "Mouse",
    scopes: ["global"],
    shortcuts: [
      { key: "Middle Drag", description: "Pan viewport" },
      { key: "Ctrl/Cmd+Left Drag", description: "Pan viewport" },
      { key: "Ctrl+Scroll", description: "Pan vertical" },
      { key: "Ctrl+Shift+Scroll", description: "Pan horizontal" },
      { key: "Scroll Wheel", description: "Zoom in / out" },
      { key: "Ctrl+Middle Drag", description: "Zoom in / out" },
      { key: "Middle+Right Drag", description: "Zoom in / out" },
    ],
  },
  {
    name: "Trackpad",
    scopes: ["global"],
    shortcuts: [
      { key: "Shift+T", description: "Toggle trackpad mode (invert Ctrl)" },
      { key: "2-finger scroll", description: "Pan viewport" },
      { key: "Ctrl + 2-finger scroll", description: "Zoom in / out" },
      { key: "Ctrl/Cmd + Click + Drag", description: "Pan viewport" },
    ],
  },
  {
    name: "Route Mode",
    scopes: ["route"],
    shortcuts: [
      { key: "Shift+R", description: "Exit route mode" },
      { key: "Double-click", description: "Start new route" },
      { key: "Click", description: "Add waypoint" },
      { key: "Double-click / Enter", description: "Commit route" },
      { key: "Click+Drag", description: "Move segment / route" },
      { key: "Right-click", description: "Route context menu" },
      { key: "U", description: "Layer up (insert via)" },
      { key: "D", description: "Layer down (insert via)" },
      { key: "A", description: "Auto-route mode" },
      { key: "N", description: "Toggle flying lines" },
      { key: "Ctrl/Cmd+C", description: "Copy selected routes" },
      { key: "Ctrl/Cmd+V", description: "Paste routes" },
      { key: "Del", description: "Delete selected routes" },
      { key: "Esc", description: "Cancel current / exit" },
    ],
  },
];
